'use client';

import { css } from '@/styled-system/css';
import type { PropertyDetailListing } from '@/src/components/features/property/types';
import { NeighborhoodSpokeContent as VercelNeighborhoodSpokeContent } from '@/src/components/features/property/NeighborhoodModule/spokes/NeighborhoodSpokeContent';
import { useDetailPanelSafe } from '@/src/components/features/property/ui/DetailPanel';

/**
 * HALO NeighborhoodSpokeContent adapter.
 *
 * The legacy HALO panel content was deeply tied to Redux + a singleton state
 * manager + the DynamicMap implementation. The vercel
 * `features/property/NeighborhoodModule/spokes/NeighborhoodSpokeContent`
 * already renders the complete map, POI pills, and commute calculator using
 * RQ-backed commute preferences, so HALO wraps it and passes the listing
 * through the inline accordion module.
 */

const wrapper = css({
  width: '100%',
  height: '100%',
  overflow: 'auto',
});

type SpokeContentProps = {
  config?: unknown;
  listing?: PropertyDetailListing;
  showHeader?: boolean;
  bodyClassName?: string;
  collapseDetails?: boolean;
  mapLoadEnabled?: boolean;
};

export const NeighborhoodSpokeContent = ({
  config,
  listing,
  showHeader = false,
  bodyClassName,
  collapseDetails,
  mapLoadEnabled,
}: SpokeContentProps) => {
  const panel = useDetailPanelSafe();
  // Keep the legacy config fallback while the reusable content component still
  // lives under the historical NeighborhoodSpoke folder.
  const effectiveListing =
    (listing as PropertyDetailListing | undefined) ??
    (config as PropertyDetailListing | undefined) ??
    (panel?.panelConfig as PropertyDetailListing | undefined);

  if (!effectiveListing) {
    return null;
  }

  return (
    <div className={wrapper}>
      <VercelNeighborhoodSpokeContent
        panelProps={{ listing: effectiveListing }}
        showHeader={showHeader}
        bodyClassName={bodyClassName}
        collapseDetails={collapseDetails}
        mapLoadEnabled={mapLoadEnabled}
      />
    </div>
  );
};
